import React from 'react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function PrivacyPolicyPage() {
  const lastUpdated = 'April 2, 2026';
  return <div className="bg-viridia-black min-h-screen pt-32 pb-24" data-id="element-956">
      <SEOHead title="Privacy Policy | Viridia Analytics" description="Privacy Policy for Viridia Analytics. Learn how we collect, use, and protect your personal data." canonical="/privacy" data-id="element-957" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-958">
        <ScrollReveal data-id="element-959">
          <div className="text-center mb-16" data-id="element-960">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" data-id="element-961">
              Privacy Policy
            </h1>
            <p className="text-gray-400" data-id="element-962">Last Updated: {lastUpdated}</p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1} data-id="element-963">
          <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-12 prose prose-invert max-w-none" data-id="element-964">
            <div className="space-y-8 text-gray-300 leading-relaxed" data-id="element-965">
              <section data-id="element-966">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-967">
                  1. Introduction
                </h2>
                <p data-id="element-968">
                  Viridia Analytics ("we," "our," or "us") respects your privacy
                  and is committed to protecting your personal data. This
                  Privacy Policy explains how we collect, use, disclose, and
                  safeguard your information when you visit our website or use
                  our marketing and automation services.
                </p>
              </section>

              <section data-id="element-969">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-970">
                  2. Information We Collect
                </h2>
                <p className="mb-2" data-id="element-971">
                  We may collect the following types of information:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-972">
                  <li data-id="element-973">
                    <strong data-id="element-974">Personal Identification Information:</strong> Name,
                    email address, phone number (including for SMS
                    communications), and company details when you fill out a
                    contact form, book a consultation, or opt in to messaging.
                  </li>
                  <li data-id="element-975">
                    <strong data-id="element-976">Business Information:</strong> Information about
                    your business, marketing goals, current advertising
                    platforms, and CRM systems.
                  </li>
                  <li data-id="element-977">
                    <strong data-id="element-978">Usage Data:</strong> Information about how you
                    interact with our website, including IP addresses, browser
                    types, pages visited, and time spent on pages.
                  </li>
                </ul>
              </section>

              <section data-id="element-979">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-980">
                  3. How We Use Your Information
                </h2>
                <p className="mb-2" data-id="element-981">
                  We use the collected information for various purposes,
                  including:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-982">
                  <li data-id="element-983">To provide, operate, and maintain our services.</li>
                  <li data-id="element-984">
                    To communicate with you, including responding to inquiries
                    and providing customer support.
                  </li>
                  <li data-id="element-985">
                    To send administrative information, such as updates to our
                    terms or policies.
                  </li>
                  <li data-id="element-986">
                    To analyze usage patterns and improve our website and
                    service offerings.
                  </li>
                  <li data-id="element-987">
                    To manage and optimize advertising campaigns on your behalf.
                  </li>
                </ul>
              </section>

              <section data-id="element-988">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-989">
                  4. SMS/Text Messaging Privacy
                </h2>
                <p className="mb-2" data-id="element-990">
                  If you opt in to receive text messages from us, the following
                  applies:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-991">
                  <li data-id="element-992">
                    We collect phone numbers when users submit contact forms,
                    book consultations, or opt in to receive text messages.
                  </li>
                  <li data-id="element-993">
                    Phone numbers may be used to send appointment confirmations,
                    follow-up messages, service updates, and promotional offers
                    via SMS.
                  </li>
                  <li data-id="element-994">
                    By providing your phone number and opting in, you consent to
                    receive automated text messages from Viridia Analytics.
                  </li>
                  <li data-id="element-995">
                    Message and data rates may apply. Message frequency varies.
                  </li>
                  <li data-id="element-996">
                    You can opt out at any time by replying{' '}
                    <strong data-id="element-997">STOP</strong> to any message.
                  </li>
                  <li data-id="element-998">
                    Reply <strong data-id="element-999">HELP</strong> for assistance.
                  </li>
                  <li data-id="element-1000">
                    <strong data-id="element-1001">
                      SMS consent and phone numbers are NOT shared with third
                      parties or affiliates for marketing purposes.
                    </strong>
                  </li>
                  <li data-id="element-1002">
                    We use third-party platforms (e.g.,
                    GoHighLevel/LeadConnector) solely to facilitate our own SMS
                    communications with you.
                  </li>
                </ul>
              </section>

              <section data-id="element-1003">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1004">
                  5. Cookies and Tracking Technologies
                </h2>
                <p data-id="element-1005">
                  We use cookies and similar tracking technologies to track
                  activity on our website and hold certain information. Cookies
                  are files with a small amount of data which may include an
                  anonymous unique identifier. You can instruct your browser to
                  refuse all cookies or to indicate when a cookie is being sent.
                </p>
              </section>

              <section data-id="element-1006">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1007">
                  6. Third-Party Services
                </h2>
                <p data-id="element-1008">
                  In providing our services, we interact with third-party
                  platforms such as Google Ads, Meta (Facebook/Instagram), Yelp,
                  and various CRM providers. We may share necessary information
                  with these platforms solely for the purpose of executing the
                  marketing services you have contracted us to perform. We do
                  not sell your personal data to third parties.
                </p>
              </section>

              <section data-id="element-1009">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1010">
                  7. Data Security
                </h2>
                <p data-id="element-1011">
                  We implement appropriate technical and organizational security
                  measures designed to protect the security of any personal
                  information we process. However, please note that no
                  electronic transmission over the Internet or information
                  storage technology can be guaranteed to be 100% secure.
                </p>
              </section>

              <section data-id="element-1012">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1013">
                  8. Your Rights
                </h2>
                <p data-id="element-1014">
                  Depending on your location, you may have certain rights
                  regarding your personal data, including the right to access,
                  correct, or delete the information we hold about you. To
                  exercise these rights, please contact us using the information
                  provided below.
                </p>
              </section>

              <section data-id="element-1015">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1016">
                  9. Changes to This Privacy Policy
                </h2>
                <p data-id="element-1017">
                  We may update our Privacy Policy from time to time. We will
                  notify you of any changes by posting the new Privacy Policy on
                  this page and updating the "Last Updated" date.
                </p>
              </section>

              <section data-id="element-1018">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1019">
                  10. Contact Us
                </h2>
                <p className="mb-2" data-id="element-1020">
                  If you have any questions about this Privacy Policy, please
                  contact us:
                </p>
                <ul className="list-none space-y-1" data-id="element-1021">
                  <li data-id="element-1022">
                    <strong data-id="element-1023">Company:</strong> Viridia Analytics
                  </li>
                  <li data-id="element-1024">
                    <strong data-id="element-1025">Location:</strong> Phoenix, AZ
                  </li>
                  <li data-id="element-1026">
                    <strong data-id="element-1027">Email:</strong>{' '}
                    <a href="mailto:info@viridiaanalytics.com" className="text-viridia hover:underline" data-id="element-1028">
                      info@viridiaanalytics.com
                    </a>
                  </li>
                  <li data-id="element-1029">
                    <strong data-id="element-1030">Phone:</strong>{' '}
                    <a href="tel:6233000708" className="text-viridia hover:underline" data-id="element-1031">
                      (623) 300-0708
                    </a>
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>;
}